import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import AddressPagination from './AddressPagination';
import AddressAliasService, { AddressAlias } from '../../services/addressAliasService';

const DEFAULT_PAGE_SIZE = 50;

const AddressAliases: React.FC = () => {
    const [aliases, setAliases] = useState<AddressAlias[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [offset, setOffset] = useState<number>(0);
    const [expandedInputs, setExpandedInputs] = useState<Set<string>>(new Set());

    const fetchAliases = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await AddressAliasService.getAliases({
                status: statusFilter || undefined,
                offset,
                limit: DEFAULT_PAGE_SIZE
            });
            setAliases(response.items);
            setTotalItems(response.totalItems);
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || "An error occurred while fetching address aliases.");
            setAliases([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [statusFilter, offset]);

    useEffect(() => {
        fetchAliases();
    }, [fetchAliases]);

    const handleStatusChange = (status: string) => {
        setStatusFilter(status);
        setOffset(0);
    };

    const handlePageChange = (page: number) => {
        setOffset((page - 1) * DEFAULT_PAGE_SIZE);
    };

    const toggleExpanded = (aliasInput: string) => {
        setExpandedInputs(prev => {
            const next = new Set(prev);
            if (next.has(aliasInput)) {
                next.delete(aliasInput);
            } else {
                next.add(aliasInput);
            }
            return next;
        });
    };

    // One row per Alias Input: the Active record (else the newest) leads the group,
    // and the input's older records are collapsed behind an expander
    const groupedAliases = useMemo(() => {
        const groups = new Map<string, AddressAlias[]>();
        for (const alias of aliases) {
            const group = groups.get(alias.aliasInput);
            if (group) {
                group.push(alias);
            } else {
                groups.set(alias.aliasInput, [alias]);
            }
        }
        return Array.from(groups.values()).map(group =>
            [...group].sort((a, b) => {
                if ((a.status === 'Active') !== (b.status === 'Active')) {
                    return a.status === 'Active' ? -1 : 1;
                }
                return (b.createdAt || '').localeCompare(a.createdAt || '');
            })
        );
    }, [aliases]);

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleString();
        } catch (e) {
            return dateString;
        }
    };

    const getStatusBadge = (alias: AddressAlias) => {
        if (alias.status === 'Active') {
            return <span className="badge bg-success">Active</span>;
        }
        if (alias.status === 'Revoked') {
            return (
                <Tooltip
                    title={
                        <div style={{ fontSize: '0.85rem' }}>
                            <strong>Revoked</strong>
                            <p className="mb-0">
                                {alias.revokedBy && <>By: {alias.revokedBy}<br /></>}
                                {alias.revokedAt && <>At: {formatDate(alias.revokedAt)}<br /></>}
                                {alias.revokeReason && <>Reason: {alias.revokeReason}</>}
                            </p>
                        </div>
                    }
                    arrow
                    placement="top"
                >
                    <span className="badge bg-danger" style={{ cursor: 'help' }}>Revoked</span>
                </Tooltip>
            );
        }
        return <span className="badge bg-secondary">{alias.status}</span>;
    };

    const currentPage = Math.floor(offset / DEFAULT_PAGE_SIZE) + 1;
    const totalPages = Math.ceil(totalItems / DEFAULT_PAGE_SIZE);

    const renderAliasRow = (alias: AddressAlias, historyCount = 0, isExpanded = false, isHistoryRow = false) => (
        <tr key={alias.id} className={isHistoryRow ? 'table-light' : undefined}>
            <td>
                <small className="font-monospace text-muted">#{alias.id}</small>
            </td>
            <td style={{ maxWidth: '280px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                {isHistoryRow ? (
                    <small className="text-muted ps-3">
                        <i className="bi bi-arrow-return-right me-1"></i>previous record
                    </small>
                ) : (
                    <>
                        <span className="fw-medium text-dark">{alias.aliasInput}</span>
                        {historyCount > 0 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ms-2 text-decoration-none text-nowrap"
                                onClick={() => toggleExpanded(alias.aliasInput)}
                            >
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} me-1`}></i>
                                {historyCount} previous
                            </button>
                        )}
                    </>
                )}
            </td>
            <td style={{ maxWidth: '280px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                <div>{alias.canonicalFullAddress || '-'}</div>
                <small className="font-monospace text-muted">
                    #{alias.canonicalAddressId}
                    {alias.canonicalTraceId && (
                        <>
                            {' · '}
                            <a
                                href={`/address-management/${alias.canonicalTraceId}`}
                                className="text-decoration-none"
                            >
                                {alias.canonicalTraceId}
                            </a>
                        </>
                    )}
                </small>
            </td>
            <td className="text-center">
                {getStatusBadge(alias)}
            </td>
            <td style={{ maxWidth: '220px', wordWrap: 'break-word', whiteSpace: 'normal' }} className="small">
                {alias.reason || <span className="text-muted">-</span>}
            </td>
            <td className="small">{alias.createdBy}</td>
            <td className="small text-muted">{formatDate(alias.createdAt)}</td>
        </tr>
    );

    return (
        <div className="container-fluid py-4 pb-5 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1">Address Aliases</h2>
                    <p className="text-muted mb-4">Alternate address inputs that resolve to a canonical address record</p>
                </div>
                <div className="d-flex gap-2 align-items-start">
                    <select
                        className="form-select form-select-sm w-auto"
                        value={statusFilter}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">All statuses</option>
                        <option value="Active">Active</option>
                        <option value="Revoked">Revoked</option>
                    </select>
                    <button className="btn btn-outline-primary btn-sm" onClick={fetchAliases} disabled={isLoading}>
                        <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                </div>
            )}

            <div className="table-responsive shadow-sm border rounded">
                <table className="table table-hover table-striped mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" className="text-nowrap">ID</th>
                            <th scope="col">
                                <div className="d-flex align-items-center gap-1">
                                    Alias Input
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '0.85rem' }}>
                                                <strong>Alias Input:</strong>
                                                <p className="mb-0">The normalized address input that is matched against incoming validation requests. Any request matching this input resolves to the canonical address.</p>
                                            </div>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <i className="bi bi-info-circle" style={{ cursor: 'help', fontSize: '0.9em' }}></i>
                                    </Tooltip>
                                </div>
                            </th>
                            <th scope="col">Canonical Address</th>
                            <th scope="col" className="text-center">Status</th>
                            <th scope="col">Reason</th>
                            <th scope="col" className="text-nowrap">Created By</th>
                            <th scope="col" className="text-nowrap">Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={7} className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Loading aliases...</p>
                                </td>
                            </tr>
                        ) : aliases.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center py-5">
                                    <i className="bi bi-signpost-split fs-1 text-muted opacity-50"></i>
                                    <p className="mt-2 text-muted">No address aliases found. Aliases are created when an address is corrected via "Correct Address".</p>
                                </td>
                            </tr>
                        ) : (
                            groupedAliases.map((group) => {
                                const primary = group[0];
                                const history = group.slice(1);
                                const isExpanded = expandedInputs.has(primary.aliasInput);
                                return (
                                    <React.Fragment key={primary.aliasInput}>
                                        {renderAliasRow(primary, history.length, isExpanded)}
                                        {isExpanded && history.map((alias) => renderAliasRow(alias, 0, false, true))}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <AddressPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={DEFAULT_PAGE_SIZE}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                isLoading={isLoading}
            />
        </div>
    );
};

export default AddressAliases;
